/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Tab, Tabs } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { Code } from 'models/common/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { MdIvesCondition, MdIvesPrjInfo, TabMenu } from 'models/md/MdIves';
import { useNavigate } from 'react-router-dom';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { tabs } from 'components/layouts/Tab';
import IvesAtc from './atcMgt/IvesAtc';
import DtlIsptMatr from './atcMgt/DtlIsptMatr';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { FileTypeName } from 'models/common/Common';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import { CommonUtil } from 'utils/CommonUtil';

/**
 * 설비사양 목록 페이지
 */
const MdEqpSpecInqPage = () => {
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const initCondition = {
    bldoCd: '',
  };

  const gridRef = useRef();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [bildPlntCodes, setBildPlntCodes] = useState<Code[]>([]);
  const [bldoCodes, setBldoCodes] = useState<Code[]>([]);
  const [condition, setCondition] = useState<any>({ ...initCondition });
  const [rowData, setRowData] = useState<any>([]);
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);

  const tabMenus = useMemo<TabMenu[]>(() => {
    return [
      { id: 'E', name: '전극' },
      { id: 'A', name: '조립' },
      { id: 'F', name: '모듈/팩' },
    ];
  }, []);
  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.플랜트', '플랜트')),
        width: 120,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.동 명', '동 명')),
        width: 120,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.생산라인', '생산라인')),
        width: 120,
        align: 'center',
        dataType: 'Number',
      },
      {
        header: String(t('com.label.믹서', '믹서')),
        columns: [
          {
            header: String(t('com.label.양극', '양극')),
            columns: [
              {
                binding: 'eltrLnNm',
                header: String(t('com.label.Maker', 'Maker')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.메인용량', '메인용량')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.설비대수', '설비대수')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.메인 믹스 Type', '메인 믹스 Type')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.DLD 대응 영부', 'DLD 대응 영부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.선분산 믹서 용량', '선분산 믹서 용량')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(
                  t('com.label.선분산 믹서 호퍼 Scale 대수', '선분산 믹서 호퍼 Scale 대수')
                ),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.선분산 믹서 설비대수', '선분산 믹서 설비대수')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.옥살산 투입라인 여부', '옥살산 투입라인 여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.옥살산 전용 호퍼 여부', '옥살산 전용 호퍼 여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.Li-500 투입라인여부', 'Li-500 투입라인여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.Li-500 전용호퍼 여부', 'Li-500 전용호퍼 여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
            ],
          },
          {
            header: String(t('com.label.음극', '음극')),
            columns: [
              {
                binding: 'eltrLnNm',
                header: String(t('com.label.Maker', 'Maker')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.메인용량', '메인용량')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.설비대수', '설비대수')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.메인 믹스 Type', '메인 믹스 Type')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.DLD 대응 영부', 'DLD 대응 영부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.선분산 믹서 용량', '선분산 믹서 용량')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(
                  t('com.label.선분산 믹서 호퍼 Scale 대수', '선분산 믹서 호퍼 Scale 대수')
                ),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.선분산 믹서 설비대수', '선분산 믹서 설비대수')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.옥살산 투입라인 여부', '옥살산 투입라인 여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.옥살산 전용 호퍼 여부', '옥살산 전용 호퍼 여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.Li-500 투입라인여부', 'Li-500 투입라인여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
              {
                binding: 'asyLnNm',
                header: String(t('com.label.Li-500 전용호퍼 여부', 'Li-500 전용호퍼 여부')),
                width: 120,
                isReadOnly: true,
                align: 'center',
              },
            ],
          },
        ],
      },
    ];
  }, [rowData]);

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    setBildPlntCodes(bildPlntCode);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
    console.log(gridRef.current);
  };

  const btnSearch = () => {
    // search
  };

  const btnRegist = () => {
    //
  };

  const btnModify = () => {
    //
  };

  const btnDelete = () => {
    //
  };

  const btnAdd = async () => {
    //
  };

  const btnDel = () => {
    //
  };

  const btnTemplateDownload = async () => {
    alert('작업중입니다.');
    return;
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.PRJ_ISU_UPLOAD,
      condition.elmPrjId,
      [],
      condition.excpId || ''
    ).finally(() => {
      openLoading(false);
    });
  };

  const uploadExcelTemplate = async (file) => {
    alert('작업중입니다.');
    return;
    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      FileTypeName.PRJ_ISU_UPLOAD,
      'NORMAL',
      'TB_EELMB_PRJ_ISU_M',
      condition.elmPrjId
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          btnSearch();
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const handleDownloadExcel = () => {
    // excel download
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.설비사양 목록', '설비사양 목록'),
      t('setup.label.설비사양 목록', '설비사양 목록')
    );
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <ComboBox
                  options={bildPlntCodes}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.bildPlntCd}
                  msgError={String(t('com.msg.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('bildPlntCd', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.동', '동'))}</label>
                <ComboBox
                  options={bldoCodes}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.bldoCd}
                  msgError={String(t('com.msg.동을 선택해 주세요.', '동을 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('bldoCd', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.공정', '공정')}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.설비군', '설비군')}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.설비사양 목록', '설비사양 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <div css={tabs.wrap} className="mt-6">
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`td-ives-cmpu-tab-${idx}`}
              aria-controls={`td-ives-cmpu-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
        <div css={tabs.btn}>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnTemplateDownload}
            disableRipple
          >
            {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
          </Button>
          <ExcelUploadButton onCallback={uploadExcelTemplate} />
          <Button css={IconButton.button} className="addRow" onClick={btnAdd} disableRipple>
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="delRow" onClick={btnDel} disableRipple>
            {t('com.button.행삭제', '행삭제')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </div>
      </div>
      <TabPanel value={currentTabIndex} index={0}>
        <CustomGrid
          id={`eqp_spec_grid_id0`}
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          initialized={onInitialized}
          height={500}
        />
      </TabPanel>
      <TabPanel value={currentTabIndex} index={1}>
        <CustomGrid
          id={`eqp_spec_grid_id1`}
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          initialized={onInitialized}
          height={500}
        />
      </TabPanel>
      <TabPanel value={currentTabIndex} index={2}>
        <CustomGrid
          id={`eqp_spec_grid_id2`}
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          initialized={onInitialized}
          height={500}
        />
      </TabPanel>
      <GlobalBtnGroup>
        <Button
          css={IconButton.button}
          className="icoNone ok"
          onClick={() => btnRegist()}
          disableRipple
        >
          {t('com.button.저장', '저장')}
        </Button>
      </GlobalBtnGroup>
      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-schedule-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default MdEqpSpecInqPage;
